import TokenizedText from './TokenizedText';
import TranslationNode from './TranslationNode';
import TranslationCache from './TranslationCache';

class TranslationImageNode extends TranslationNode {
    /**
     * Generate a new TranslationImageNode
     *
     * @param {HTMLImageElement} element HTML image element
     * @param {Element} _ parent node
     */
    constructor(element, _) {
        super(element, element);

        this.mergeable = false;
    }

    /**
     * Check if we should translate this text
     * @param {string} text Text to check
     * @returns {boolean}
     */
    shouldWeTranslate(text) {
        const trimmed = text.trim();

        // data:image, don't translate
        if (trimmed.startsWith('data:image')) {
            return false;
        }

        // call parent
        return super.shouldWeTranslate(text);
    }

    /**
     * Convert/tokenize the element to be translated.
     * 
     * @param {boolean} [ignoreCached=false] Ignore cached tokenized, this will also not save the tokenized to cache
     * @returns {TokenizedText[]} Collection of text to be translated
     */
    tokenize(ignoreCached = false) {
        if (this._cachedTokenized !== undefined && !ignoreCached) {
            return this._cachedTokenized;
        }

        const srcText = this.element.getAttribute('src');
        const altText = this.element.getAttribute('alt');
        const titleText = this.element.getAttribute('title');

        if (srcText) {
            /** @type {Record<string, string>} */
            const metaText = {};

            if (this.shouldWeTranslate(srcText)) {
                metaText.src = srcText;
            }

            if (altText && this.shouldWeTranslate(altText)) {
                metaText.alt = altText;
            }

            if (titleText && this.shouldWeTranslate(titleText)) {
                metaText.title = titleText;
            }

            if (!Object.keys(metaText).length) {
                throw new Error('No attribute to translate in image element');
            }

            const token = new TokenizedText(this, 0, '', metaText);
            token.subId = 'merged-src';
            token.tagName = 'img';

            if (!ignoreCached) {
                this._cachedTokenized = [token];
            }

            return [token];
        } else if (altText || titleText) {
            // no src attribute, use separate tokenized text

            const mergedNode = [];
            if (altText && this.shouldWeTranslate(altText)) {
                const altNode = new TokenizedText(this, 0, altText);
                altNode.subId = 'alt';
                mergedNode.push(altNode);
            }

            if (titleText && this.shouldWeTranslate(titleText)) {
                const titleNode = new TokenizedText(this, 1, titleText);
                titleNode.subId = 'title';
                mergedNode.push(titleNode);
            }

            if (!mergedNode.length) {
                throw new Error('No attribute to translate in image element');
            }

            if (!ignoreCached) {
                this._cachedTokenized = mergedNode;
            }

            return mergedNode;
        }

        throw new Error('No attribute to translate in image element');
    }

    /**
     * Replace the text for this element node with the new text
     *
     * @param {TokenizedText} newToken The replacement token
     * @param {string} lang language code
     * @param {import('./TranslationNode').TokenApplyOptions} [options] Options when applying new token
     */
    applyChange(newToken, lang, options) {
        const mergedOptions = Object.assign({}, {
            dryRun: false,
            skipCache: false,
        }, options || {});

        if (!mergedOptions.skipCache) {
            this.cachedIndex.push(newToken.index);
        }

        const subId = newToken.subId;

        if (!subId) {
            // This should not happen
            return;
        }

        let tokText = newToken.text;

        if (subId === 'merged-src') {
            tokText = newToken.metaExtra.src;
            if (!tokText) {
                // This should not happen
                return;
            }

            const original = this.element.getAttribute('src');
            if (mergedOptions.dryRun) {
                tokText = `${original}#linguise-translated-demo`;
            }
            this.element.setAttribute('src', tokText);
            if (!mergedOptions.skipCache && !mergedOptions.dryRun) {
                TranslationCache.add(original, tokText, lang, subId);
            }

            const altText = newToken.metaExtra.alt;
            if (altText) {
                this.applyAttributeChange('alt', altText, {
                    ...mergedOptions,
                    language: lang,
                    subId,
                });
            }
            const titleText = newToken.metaExtra.title;
            if (titleText) {
                this.applyAttributeChange('title', titleText, {
                    ...mergedOptions,
                    language: lang,
                    subId,
                });
            }
            this.attachEditor(newToken);

            return;
        }

        if (subId === 'alt') {
            this.applyAttributeChange('alt', tokText, {
                ...mergedOptions,
                language: lang,
            });

            if (newToken.editorMeta && newToken.editorMeta.altHash) {
                this.element.setAttribute('data-linguise-alt-hash', newToken.editorMeta.altHash);
            }
        }

        if (subId === 'title') {
            this.applyAttributeChange('title', tokText, {
                ...mergedOptions,
                language: lang,
            });
        }
    }

    /**
     * Noop, we don't need to blur the parent element for image
     */
    blurParent() {}

    /**
     * Noop, we don't need to unblur the parent element for image
     */
    unblurParent() {}
}

export default TranslationImageNode;
